import React from "react";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_file_upload } from "../../../constants";

function SignatureRecordTable({ record, onBack, employeeAvatarColorByUserId }) {
  // View file by fetching URL from API
  const handleViewFile = async (fileUuid) => {
    if (!fileUuid) return;
    try {
      const res = await getRightworkAPI(API_get_file_upload, { file_upload_uuid: fileUuid });
      const fileUrl = res?.data?.redux?.payload?.presigned_url;
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    } catch (err) {
      console.error("Failed to fetch file:", err);
    }
  };

  const formatDateReadable = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isAcknowledgement = record.signature_mode === "acknowledgement";
  const hasSubmissionData = record.submissions && record.submissions.length > 0;
  const hasIncompleteData =
    record.incomplete_assignments && record.incomplete_assignments.length > 0;
  const hasEmployeeData = hasSubmissionData || hasIncompleteData;

  const exportSignatureCSV = () => {
    const rows = [];
    (record.submissions || []).forEach((submission) => {
      rows.push({
        "Employee": submission.employee_name,
        "Signatures Completed": `${record.signature_fields} / ${record.signature_fields}`,
        "Text Fields Completed":
          record.text_fields > 0
            ? `${record.text_fields} / ${record.text_fields}`
            : "N/A",
        "Status": "Signed",
        "Date Signed": submission.date_signed
          ? formatDateReadable(submission.date_signed)
          : "",
      });
    });
    (record.incomplete_assignments || []).forEach((assignment) => {
      rows.push({
        "Employee": assignment.employee_name,
        "Signatures Completed": `0 / ${record.signature_fields}`,
        "Text Fields Completed":
          record.text_fields > 0
            ? `0 / ${record.text_fields}`
            : "N/A",
        "Status": "Pending Signature",
        "Date Signed": "",
      });
    });

    if (rows.length === 0) return;
    const csv = objectToCSV(rows);
    downloadCSV(csv, `signature_${record.name.replace(/\s+/g, "_")}.csv`);
  };

  return (
    <div className="px-6 py-8">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium mb-4 transition-colors"
        >
          <ChevronLeftIcon className="-ml-2" style={{ fontSize: "24px" }} />
          Back to Records
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              {record.name}
            </h2>
            <p className="text-sm text-slate-600">Signature records</p>
          </div>
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
            onClick={exportSignatureCSV}
          >
            <DownloadIcon style={{ fontSize: "20px" }} />
            <span className="font-medium">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Signature Details Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        {isAcknowledgement ? (
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Statement
              </div>
              <div className="text-sm text-slate-900">
                {record.statement_preview || record.name || "—"}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Total Signed
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {record.total_submissions} / {record.assigned_count}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-6">
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Document
              </div>
              <div className="text-sm font-medium text-slate-900">
                {record.pdf_name || record.name || "—"}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Total Pages
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {record.total_pages || "—"}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Signature Fields
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {record.signature_fields || "—"}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                Total Completed
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {record.total_submissions} / {record.assigned_count}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submissions Table */}
      <div className="-mx-6 border-y border-slate-200 bg-white">
        {hasEmployeeData ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Fields Completed
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date Signed
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {record.submissions.map((submission, index) => (
                  <tr
                    key={`completed-${index}`}
                    onClick={
                      submission.file_uuid
                        ? () => handleViewFile(submission.file_uuid)
                        : undefined
                    }
                    className={`hover:bg-slate-50 transition-colors duration-150${
                      submission.file_uuid ? " cursor-pointer" : ""
                    }`}
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={submission.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[
                                submission.employee_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-900">
                            {submission.employee_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      {isAcknowledgement ? (
                        <div className="flex items-center gap-2">
                          <DrawOutlinedIcon
                            className="text-slate-400"
                            style={{ fontSize: "18px" }}
                          />
                          <span className="text-sm text-slate-900">
                            Signed
                            {submission.signer_printed_name
                              ? ` as ${submission.signer_printed_name}`
                              : ""}
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <DrawOutlinedIcon
                              className="text-slate-400"
                              style={{ fontSize: "18px" }}
                            />
                            <span className="text-sm text-slate-900">
                              {record.signature_fields} / {record.signature_fields}{" "}
                              signatures
                            </span>
                          </div>
                          {record.text_fields > 0 && (
                            <div className="flex items-center gap-2">
                              <DescriptionOutlinedIcon
                                className="text-slate-400"
                                style={{ fontSize: "18px" }}
                              />
                              <span className="text-sm text-slate-900">
                                {record.text_fields} / {record.text_fields}{" "}
                                text fields
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon
                          className="text-emerald-600"
                          style={{ fontSize: "20px" }}
                        />
                        <span className="text-sm font-medium text-emerald-700">
                          Signed
                        </span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CalendarTodayIcon style={{ fontSize: "16px" }} />
                        <span>{formatDateReadable(submission.date_signed)}</span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      {submission.file_uuid ? (
                        <Tooltip title="View Signed Document">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewFile(submission.file_uuid);
                            }}
                            className="w-8 h-8 bg-violet-100 hover:bg-violet-200 text-violet-600 rounded-lg flex items-center justify-center transition-colors"
                          >
                            <VisibilityIcon style={{ fontSize: "18px" }} />
                          </button>
                        </Tooltip>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {record.incomplete_assignments?.map((assignment, index) => (
                  <tr
                    key={`pending-${index}`}
                    className="hover:bg-slate-50 transition-colors duration-150"
                  >
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 shrink-0">
                          <PersonAvatar
                            view="daily"
                            name={assignment.employee_name}
                            bgColor={
                              employeeAvatarColorByUserId?.[
                                assignment.employee_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-900">
                            {assignment.employee_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      {isAcknowledgement ? (
                        <div className="flex items-center gap-2">
                          <DrawOutlinedIcon
                            className="text-slate-400"
                            style={{ fontSize: "18px" }}
                          />
                          <span className="text-sm text-slate-500">Not signed</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <DrawOutlinedIcon
                              className="text-slate-400"
                              style={{ fontSize: "18px" }}
                            />
                            <span className="text-sm text-slate-500">
                              0 / {record.signature_fields} signatures
                            </span>
                          </div>
                          {record.text_fields > 0 && (
                            <div className="flex items-center gap-2">
                              <DescriptionOutlinedIcon
                                className="text-slate-400"
                                style={{ fontSize: "18px" }}
                              />
                              <span className="text-sm text-slate-500">
                                0 / {record.text_fields} text fields
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                          Pending Signature
                        </span>
                      </div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">—</div>
                    </td>
                    <td className="align-middle px-6 py-3 whitespace-nowrap">
                      <div className="text-sm text-slate-500">—</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <InfoOutlinedIcon
              className="text-slate-300 mx-auto mb-4"
              style={{ fontSize: "48px" }}
            />
            <p className="text-slate-600 font-medium mb-2">
              Employee-level submission data not available
            </p>
            <p className="text-slate-400 text-sm">
              Summary: {record.total_submissions} of {record.assigned_count} employees
              have signed this document.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignatureRecordTable;
